import { ReactElement } from 'react';
import { CategoryAnswerType } from '../pages/applicant/NewApplicationWizard/CategoryAnswerType';
import { Status } from './ExamModuleTypes';
import { Moment } from 'moment';
import {
	ApplicationPreworkStatus,
	PreworkCompletionStatus,
	PreworkReviewAction,
	PreworkStatus,
	PreworkStatusFunctionaryPredefinedComment,
} from './PreWorkServiceTypes';
import { OptionData } from '../hooks/useZonesDropdown';
import { OptionData as RegionData } from '../pages/functionary/DashboardPage/FilterDashboard';

export interface GetUserRoleRequest {
	batchID: number;
	firebaseId: string;
	srcmId: string;
}

export enum RoleBasedModules {
	SCREENING_MODULE = 'SCREENING_MODULE',
	EXAM_MODULE = 'EXAM_MODULE',
	NPS_MODULE = 'NPS_MODULE',
	PREWORK_MODULE = 'PREWORK_MODULE',
	MANAGE_FUNCTIONARIES_MODULE = 'MANAGE_FUNCTIONARIES_MODULE',
	MANAGE_PRIVILEGES_MODULE = 'MANAGE_PRIVILEGES_MODULE',
}

export enum UserRole {
	SNP_ADMIN = 'SNP_ADMIN',
	PREWORK_ADMIN = 'PREWORK_ADMIN',
	PREWORK_MENTOR = 'PREWORK_MENTOR',
	SCREENING_ADMIN = 'SCREENING_ADMIN',
	APPROVER = 'APPROVER',
	PROPOSER = 'PROPOSER',
	CARE_TEAM = 'CARE_TEAM',
	ZC = 'ZC',
	NPS_ADMIN = 'NPS_ADMIN',
	NPS_MENTOR = 'NPS_MENTOR',
	PRECEPTOR = 'PRECEPTOR',
	APPLICANT = 'APPLICANT',
	APP_ROLE_ADMIN = 'APP_ROLE_ADMIN',
	EXAM_ADMIN = 'EXAM_ADMIN',
}

export type Module =
	| 'NOT_APPLICABLE_MODULE'
	| 'SNP_MODULE'
	| 'NPHH_MODULE'
	| 'EXAM_MODULE';

export enum Category {
	BOTH_APPROVED_AND_REJECT_IN_SCREENING = 'BOTH_APPROVED_AND_REJECT_IN_SCREENING',
	EXAM_MODULES = 'EXAM_MODULES',
	REJECT_IN_SCREENING = 'REJECT_IN_SCREENING',
	APPROVED_IN_SCREENING = 'APPROVED_IN_SCREENING',
}

export interface GetUserRoleResponse {
	role: UserRole;
}

export interface SearchCenterRequest {
	batchId: number;
	searchKey: string;
	pageIndex: number;
	pageSize: number;
}
export interface SearchCountryRequest {
	pageSize: number;
	name__icontains: string;
}
export interface ApplicationsFilter {
	status?: Array<string>;
	searchByApplicantName?: string;
	gender?: string;
	zoneNames?: Array<string>;
	applicantSrcmId?: string;
	assignedToLevel1FunctionarySrcmId?: string;
	preceptorReview?: Array<string>;
	proposerReview?: Array<string>;
	searchByProposerSrcmId?: string;
	searchByProposerName?: string;
	regionIds?: Array<number>;
	searchByFunctionaryEmail?: string;
	searchByFunctionaryName?: string;
	roleFilters?: Array<UserRole>;
	zoneId?: Array<number>;
	approverReview?: Array<string>;
	zcReview?: Array<string>;
	applicationId?: Array<number>;
	batchIds?: Array<number>;
	preworkStatus?: Array<PreworkStatus>;
	searchByProposerGender?: string;
	selectedZones?: OptionData[];
	region?: RegionData | null;
}
export interface GetApplicationsRequest {
	firebaseId: string;
	pageIndex: number;
	pageSize: number;
	batchId: number;
	filter: ApplicationsFilter;
	functionarySrcmId: string;
	predefinedReasonLanguage: string;
	selectedRoleCode: UserRole;
}

export interface GetFunctionaryListRequest {
	pageIndex: number;
	pageSize: number;
	batchId: number;
	filter: ApplicationsFilter;
	functionarySrcmId: string;
}

export interface Center {
	id: number;
	name: string;
	externalId?: string;
	zone: string;
	centerType: string;
	region?: string;
	regionId?: number;
	zoneId?: number;
}

export interface SelectedDropdownValue {
	value: number | string;
	label: string;
}
export interface Country {
	value: number | string;
	label: string;
}
export interface State {
	id?: number;
	name?: string;
}

export interface ZoneNames {
	labe: string;
	key: string;
	value: string;
}

export interface PredefinedReason {
	id: number;
	reasonText: string;
	category: string;
	daysToEnableReApply?: number;
	reasonTextForCandidate?: string;
}

export interface PredefinedReasonListRequest {
	pageIndex: number;
	pageSize: number;
	language?: string;
}

export interface PredefinedReasonListResponse {
	predefinedReasons: Array<PredefinedReason>;
	pagination: Pagination;
}

export interface CentersPage {
	totalNoOfPages: number;
	centers?: Array<Center>;
}

export interface ApplicantInfo {
	firstName?: string;
	lastName?: string;
	middleName?: string;
	email?: string;
	mobile?: string;
	gender?: string;
	srcmid: string;
	firebaseId: string;
	dateOfBirth?: string;
	age?: number;
	dateOfJoining?: string;
	educationQualification?: string;
	profession?: string;
	professionDetails?: string;
	addressLine1?: string;
	addressLine2?: string;
	addressLine3?: string;
	city?: string;
	pincode?: string;
	photoUrl?: string;
}

export interface ApplicationSummary {
	applicationId: number;
	applicant: ApplicantInfo;
	center: Center;
	applicationStatus: ApplicationStatusType;
	proposerName?: string;
	proposerSrcmId?: string;
	applicantAnswersReviewed?: boolean;
	applicantInterviewDone?: boolean;
	preceptorInterviewDone?: boolean;
	familyMemberInterviewDone?: boolean;
	otherReferenceInterviewDone?: boolean;
	otherReferenceProvided?: boolean;
	isApprover?: boolean;
	proposerRecommendation?: Feedback;
	preceptorRecommendation?: Feedback;
	careTeamRecommendation?: Feedback;
	zcRecommendation?: Feedback;
	approverRecommendation?: Feedback;
	shouldCareTeamReview?: boolean;
	applicantForwardedToCareTeam?: boolean;
	proposerForwardedToCareTeam?: boolean;
	approverForwardedToCareTeam?: boolean;
	zcForwardedToCareTeam?: boolean;
	canReapplyDate?: string;
	applicationPreworkStatus?: ApplicationPreworkStatus;
	noOfExamsEligible?: number;
	noOfExamsRegistered?: number;
	noOfExamsAttempted?: number;
	nofExamsCleared?: number;
	noOfExamsNotCleared?: number;
	noOfExamsNotRegistered?: number;
	batch?: Batch;
	approverRevertedForwardedToCareTeam?: boolean;
}

export interface ApplicationSummaryList {
	applicationSummary?: Array<ApplicationSummary>;
	pagination: Pagination;
}

export type ZonesValue = Array<{ value: string }>;

export type Zones = Array<{ label: string; value: number }>;

export interface BaseFunctionaryInfo {
	id?: number;
	printName?: string;
	email?: string;
	mobile?: string;
	gender?: string;
	srcmId: string;
	firebaseId?: string;
	isApprover?: boolean;
	isCareTeam?: boolean;
	isPreworkAdmin?: boolean;
	isScreeningAdmin?: boolean;
	isZC?: boolean;
	adminSrcmId?: string;
	regions?: Array<{ label: string; value: number }>;
	functionaryType?: string;
	homeZone?: string;
	homeZoneToDisplay?: { value: string | undefined };
	homeRegion?: string;
	homeRegionId?: number;
	homeRegionToDisplay?: {
		label: string | undefined;
		value: number | undefined;
	};
	isActive?: boolean;
	isNpsAdmin?: boolean;
	applicationCount?: number;
	interviewCount?: number;
	isPreworkMentor?: boolean;
	isNpsMentor?: boolean;
	partnerId?: number;
}

export interface FunctionaryInfo extends BaseFunctionaryInfo {
	zones?: Zones;
}

export interface FunctionaryList {
	functionary?: Array<FunctionaryInfo>;
	pagination: Pagination;
}

export interface FunctionaryOfZone {
	functionary: FunctionaryInfo;
	zone?: Array<string>;
	approverZone?: Array<string>;
	zcZone?: Array<string>;
	preworkZone?: Array<string>;
	regions?: Array<Region>;
}

export interface SaveFunctionaryOfZoneRequest {
	printName?: string;
	email?: string;
	mobile?: string;
	gender?: string;
	srcmId: string;
	proposerOfZones?: Array<string>;
	approverOfZones?: Array<string>;
	zcOfZones?: Array<string>;
	isCareTeam?: boolean;
	homeZone?: string;
	homeRegion?: string;
	preworkAdminOfZones?: Array<string>;
	isActive?: boolean | undefined;
	batchId?: number;
	npsAdminOfZones?: Array<string>;
	screeningAdminOfZones?: Array<string>;
	isPreworkMentor?: boolean;
	isNpsMentor?: boolean;
}

export interface BulkSaveFunctionariesOfZoneRequest {
	functionaryOfZones: Array<SaveFunctionaryOfZoneRequest>;
	functionarySrcmId?: string;
}

export interface RegisterFunctionaryRequest {
	saveFunctionaryOfZoneRequest: SaveFunctionaryOfZoneRequest;
}

export interface BooleanValue {
	value: boolean;
}

export interface IntegerValue {
	value: number;
}

export interface StringValue {
	value: string;
}

export interface ApplicantReference {
	category?: string;
	name?: string;
	mobile?: string;
	relationship?: string;
	isAbhyasi?: boolean;
	isPreceptor?: boolean;
	id?: number;
	email?: string;
	srcmid?: string;
	mySrcmPartnerId?: number;
	firstName?: string;
	middleName?: string;
	lastName?: string;
}

export interface UpdatePreceptorReferenceRequest {
	functionarySrcmId: string;
	applicationId: number;
	preceptorReference: ApplicantReference;
}

export interface Answer {
	categoryId: number;
	questionId: number;
	answerOptionId?: number;
	additionalComment?: string;
}

export interface Answers {
	language?: string;
	questionsAndAnswers: Array<Answer>;
	finalComments?: string;
}

export type ReviewAction = 'NOT_PROVIDED' | 'APPROVE' | 'REJECT' | 'ON_HOLD';

export interface Feedback {
	review?: string;
	comments?: string;
	predefinedReasons?: Array<PredefinedReason>;
	mandatoryQuestionsWithAnswers?: CategoryAnswerType;
	shouldCareTeamReview?: boolean;
	approverRevertedForwardedToCareTeamComments?: string;
}

export interface FeedbackWithConfirmation extends Feedback {
	videoCallCheckbox?: boolean;
}

export interface OtherReferenceFeedback extends Feedback {
	referenceRecord?: string;
}
export interface ApproverFeedback extends Feedback {
	reapplyCondition?: string;
}

export interface CategoryReviewFeedback {
	inspirationAndWillingness?: Feedback;
	practice?: Feedback;
	characterAndHabits?: Feedback;
	availabilityAfterProgram?: Feedback;
	support?: Feedback;
	understandingOfHeartfulness?: Feedback;
	eligibilityCriteria?: Feedback;
	dietaryHabitsAndHealth?: Feedback;
	answersToMandatoryQuestions?: Answers;
}

export interface Functionary {
	id: number;
	printName: string;
	email: string;
	mobile: string;
	srcmId: string;
	firebaseId: string;
	isAdmin: boolean;
	isApprover?: boolean;
	gender?: Gender;
	adminSrcmId?: string;
	isCareTeam?: boolean;
	isZC?: boolean;
	homeZone?: string;
	homeRegion?: string;
	homeRegionId?: number;
	isPreworkAdmin?: boolean;
	isScreeningAdmin?: boolean;
	isActive?: boolean;
	isNpsAdmin?: boolean;
	applicationCount?: number;
	interviewCount?: number;
}

export type ApplicationStatusType =
	| 'NOT_YET_APPLIED'
	| 'USER_SUBMITTED_APPLICATION'
	| 'FUNCTIONARY_ASSIGNED'
	| 'FUNCTIONARY_SUBMITTED'
	| 'APPROVED'
	| 'REJECTED'
	| 'SYS_ANOTHER_FUNCTIONARY_ASSIGNED'
	| 'ON_HOLD_AFTER_LEVEL1_REVIEW'
	| 'ON_HOLD_AFTER_LEVEL2_REVIEW'
	| 'WAITING_FOR_CARE_TEAM';

export interface Application {
	applicationId: number;
	applicant: ApplicantInfo;
	preceptorReference: ApplicantReference;
	familyMemberReference: ApplicantReference;
	otherReference: ApplicantReference;
	applicationStatus: ApplicationStatusType;
	userAnswers: Answers;
	applicantAnswersReviewed: boolean;
	applicantInterviewDone: boolean;
	preceptorInterviewDone: boolean;
	familyMemberInterviewDone: boolean;
	otherReferenceInterviewDone: boolean;
	answersReviewFeedback?: CategoryReviewFeedback;
	applicantInterviewFeedback?: CategoryReviewFeedback;
	preceptorInterviewFeedback?: CategoryReviewFeedback;
	familyMemberInterviewFeedback?: CategoryReviewFeedback;
	otherReferenceInterviewFeedback?: CategoryReviewFeedback;
	preceptorRecommendation: Feedback;
	proposerRecommendation: Feedback;
	careTeamRecommendation: Feedback;
	zcRecommendation: Feedback;
	approverRecommendation: Feedback;
	applicationSubmissionDateDDMMYYYY: string;
	batchId: number;
	proposer: Functionary;
	referencesCollected: boolean;
	center: Center;
	preferredLanguageOfTraining?: string;
	availabilityForTrainingCourse?: string;
	otherReferenceProvided?: boolean;
	isApprover?: boolean;
	shouldCareTeamReview?: boolean;
	reapplyCondition?: string;
	daysToEnableReApply?: number;
	draftState: DraftState;
	completedDraftState: DraftState;
	otherReferenceRecommendation: Feedback;
	applicantForwardedToCareTeam?: boolean;
	proposerForwardedToCareTeam?: boolean;
	approverForwardedToCareTeam?: boolean;
	zcForwardedToCareTeam?: boolean;
	approverRevertedForwardedToCareTeam?: boolean;
}

export interface PagingConfig {
	pageNo?: number;
	pageSize?: number;
}

export interface Pagination extends PagingConfig {
	totalNoOfRecords: number;
	totalNoOfPages?: number;
}

export type Gender = 'NOT_PROVIDED' | 'MALE' | 'FEMALE';

export interface User {
	mySrcmPartnerId?: number;
	photoUrl?: string;
	firstName?: string;
	middleName?: string;
	lastName?: string;
	addressLine1?: string;
	addressLine2?: string;
	addressLine3?: string;
	addressLine4?: string;
	city?: string;
	mobile?: string;
	email?: string;
	pincode?: string;
	firebaseId?: string | null;
	srcmId?: string;
	gender?: Gender;
	age?: number;
	monthOfJoining?: string;
	educationQualification?: string;
	profession?: string;
	professionDetails?: string;
	cityId?: number;
	stateId?: number;
	state?: string;
	countryId?: number;
	country?: string;
}

export interface ApplicantAdditionalDetails {
	center?: number;
	monthOfJoiningInMMMYYYY?: string;
	educationQualification?: string;
	profession?: string;
	professionDetails?: string;
	dateOfBirth?: string;
	preceptorReference?: ApplicantReference;
	familyMemberReference?: ApplicantReference;
	otherReference?: ApplicantReference;
	otherReferenceProvided: boolean;
	preferredLanguageOfTraining?: string;
	availabilityForTrainingCourse?: string;
	addressLine1?: string;
	addressLine2?: string;
	addressLine3?: string;
	city?: string;
	pincode?: string;
}

export interface NewApplicationRequest {
	userDetails: User;
	additionalDetails: ApplicantAdditionalDetails;
	userAnswers: Answers;
	batchId: number;
}

export interface UserID {
	firebaseId: string;
	srcmId: string;
}

export interface CanSubmitNewApplicationRequest extends UserID {
	predefinedReasonLanguage?: string;
}

export enum DraftState {
	NOT_APPLICABLE_DRAFT_STATE = 'NOT_APPLICABLE_DRAFT_STATE',
	ADDITIONAL_DETAILS = 'ADDITIONAL_DETAILS',
	TRAINING_COURSE = 'TRAINING_COURSE',
	PRECEPTOR_CONTACT = 'PRECEPTOR_CONTACT',
	FAMILY_MEMBER_DETAILS = 'FAMILY_MEMBER_DETAILS',
	OTHER_DETAILS = 'OTHER_DETAILS',
	ABOUT_THE_PROCESS = 'ABOUT_THE_PROCESS',
	ESSENTIALS = 'ESSENTIALS',
	INSPIRATION_AND_WILLINGNESS = 'INSPIRATION_AND_WILLINGNESS',
	PRACTICE = 'PRACTICE',
	CHARACTER_AND_HABITS = 'CHARACTER_AND_HABITS',
	AVAILABILITY_AFTER_PROGRAM = 'AVAILABILITY_AFTER_PROGRAM',
	FAMILY_SUPPORT = 'FAMILY_SUPPORT',
	UNDERSTANDING_OF_HEARTFULNESS = 'UNDERSTANDING_OF_HEARTFULNESS',
	DIETARY_HABITS_AND_HEALTH = 'DIETARY_HABITS_AND_HEALTH',
	TERMS_AND_CONDITIONS = 'TERMS_AND_CONDITIONS',
}

export interface SaveApplicationRequest {
	userDetails: User;
	additionalDetails: ApplicantAdditionalDetails;
	userAnswers: Answers;
	batchId: number;
	applicationId: number;
	draftState: DraftState;
	completedDraftState: DraftState;
}

export interface FunctionaryPredefinedComment {
	id: number;
	predefinedReason: PredefinedReason;
	providedBy: string;
	functionarySrcmId: number;
	createdDate: string;
	modifiedDate: string;
}

export enum RejectedReasonRectifyOption {
	NOT_APPLICABLE_OPTION = 0,
	RECTIFIED = 1,
	NOT_RECTIFIED = 2,
}

export interface PastApplicationRejectedReason {
	functionaryPredefinedComment: FunctionaryPredefinedComment;
	rectifyOption: RejectedReasonRectifyOption;
}

export interface PastApplicationPreworkStatusRejectedReason {
	preworkStatusFunctionaryPredefinedComment: PreworkStatusFunctionaryPredefinedComment;
	rectifyOption: RejectedReasonRectifyOption;
}

export interface RectifyPastApplicationRejectedReasonsRequest {
	applicantSrcmId?: string;
	pastApplicationRejectedReasons?: Array<PastApplicationRejectedReason>;
	pastApplicationPreworkStatusRejectedReasons?: Array<PastApplicationPreworkStatusRejectedReason>;
}

export interface CanSubmitNewApplicationResponse {
	allowed?: boolean;
	notAllowedReason?: string;
	pastApplicationStatus?: ApplicationStatusType;
	reapplyDate?: string;
	pastApplicationRejectedReasons?: Array<FunctionaryPredefinedComment>;
	pastApplicationBatch?: Batch;
	pastApplicationRejectedReasonsRectified?: boolean;
	pastApplicationId?: number;
	preworkCompletionStatus?: PreworkCompletionStatusType;
	preworkMentorCompletionStatus?: PreworkCompletionStatusType;
	approverPreworkRecommendation?: PreworkReviewAction;
	isPreworkStautsRejectedByApprover?: boolean;
	reapplyDateForRejectedPrework?: string;
	preworkStatusFunctionaryPredefinedComments?: Array<PreworkStatusFunctionaryPredefinedComment>;
	pastApplicationPreworkRejectedReasonsRectified?: boolean;
}

export interface Batch {
	id?: number;
	name: string;
	description: string;
	startDate?: string;
	endDate?: string;
	snpResultDateForApprover?: string;
	snpResultDateForProposer?: string;
	snpResultDateForZC?: string;
	snpResultDateForCandidate?: string;
	proposerReviewStartDate?: string;
	proposerReviewEndDate?: string;
	approverReviewStartDate?: string;
	approverReviewEndDate?: string;
	status?: Status;
}

export interface BatchFormType {
	name?: string;
	description?: string;
	startDate?: Moment;
	endDate?: Moment;
	status?: Status;
}

export interface BatchFilter {
	batchId?: number;
	batchName?: string;
	batchCode?: string;
	batchDescription?: string;
	startDate?: string | Moment;
	endDate?: string | Moment;
	isPreworkEnabled?: boolean;
	sortingOrder?: string;
	status?: Status;
}

export interface FunctionaryFeedbackRequest {
	applicationId: number;
	feedbackCategory: string;
	categoryReviewFeedback: CategoryReviewFeedback;
	functionarySrcmId: string;
	selectedRoleCode: UserRole;
}

export interface PreceptorRecommendationRequest {
	feedback: FunctionaryFeedbackRequest;
	recommendation: Feedback;
	preceptorReference: ApplicantReference;
	selectedRoleCode: UserRole;
}

export interface ApplicantAdditionalDetailsRequest {
	additionalDetails: ApplicantAdditionalDetails;
	applicationId: number;
	functionarySrcmId: string;
}

export interface ProposerRecommendationRequest {
	recommendation: Feedback;
	applicationId: number;
	functionarySrcmId: string;
	selectedRoleCode: UserRole;
}

export interface OtherReferenceRecommendationRequest {
	applicationId: number;
	answersToMandatoryQuestions?: Answers;
	feedbackCategory: string;
	recommendation: Feedback;
	functionarySrcmId: string;
	selectedRoleCode: UserRole;
}

export interface ZCRecommendationRequest extends ProposerRecommendationRequest {
	shouldCareTeamReview?: boolean;
}

export interface ApproverRecommendationRequest {
	recommendation: Feedback;
	applicationId: number;
	functionarySrcmId: string;
	reapplyCondition?: string;
	shouldCareTeamReview?: boolean;
	approverRevertedForwardedToCareTeam?: boolean;
	selectedRoleCode: UserRole;
}

export interface SearchZonesRequest {
	functionarySrcmId: string;
	searchKey?: string;
	pageIndex: number;
	pageSize: number;
	includeOnlyZonesOfApprover?: boolean;
	displayAllZones?: boolean;
	isPaginationRequired?: boolean;
	regionIds?: Array<number>;
}

export interface ZonesPage {
	totalNoOfPages: number;
	zones?: Array<string>;
}

export interface GetApplicationRequest {
	applicationId: number;
	functionarySrcmId?: string;
	predefinedReasonLanguage?: string;
	selectedRoleCode?: UserRole;
}

export interface GetFunctionaryRequest {
	functionarySrcmId?: string;
}

export interface GetRegionsRequest {
	functionarySrcmId?: string;
	preceptorBatchId?: number;
	batchId?: number;
	pageIndex?: number;
	pageSize?: number;
	selectedRoleCode: UserRole;
}

export interface OtpVerificationRequest {
	otp: string;
}

export type PreworkCompletionStatusType =
	| 'NOT_STARTED'
	| 'INCOMPLETE'
	| 'PARTIALLY_COMPLETED'
	| 'COMPLETED';

export interface PreworkAnswersRequest {
	batchId: number;
	applicationId: number;
	preworkAnswers: Answers;
	completionStatus: PreworkCompletionStatusType;
}

export interface PreworkResponseData {
	applicationId: number;
	batch: Batch;
	userAnswers: Answers;
	completionStatus: PreworkCompletionStatusType;
	mentorAnswers: Answers;
	mentorcompletionStatus: PreworkCompletionStatusType;
	isForwardedToApprover?: boolean;
	preworkFunctionaryPredefinedComments?: Array<PreworkStatusFunctionaryPredefinedComment>;
	approver?: Functionary;
	approverComment?: string;
	approverPreworkRecommendation?: PreworkReviewAction;
	canReapplyDate?: string;
	daysToEnableReApply?: string;
	preworkMentor?: Preceptor;
}

export interface PreceptorLanguage {
	id: number;
	name: string;
}

export type SortingOrder =
	| 'SORTING_ORDER_UNSPECIFIED'
	| 'ASCENDING'
	| 'DESCENDING';

export type NPHHSortingFieldName =
	| 'SORTING_FIELD_NAME_UNSPECIFIED'
	| 'PRECEPTOR_ID'
	| 'NAME'
	| 'SRCM_ID'
	| 'BATCH'
	| 'ADDRESS'
	| 'POSTAL_CODE'
	| 'COUNTRY'
	| 'CITY'
	| 'STATE'
	| 'ZONE'
	| 'MOBILE'
	| 'EMAIL'
	| 'GENDER'
	| 'GROUP_ID'
	| 'GROUP_NUMBER';

export interface NPHHSortingRequest {
	sortingFieldName: NPHHSortingFieldName;
	sortingOrder: SortingOrder;
}

export interface Preceptor {
	id: number;
	key?: number;
	firstName: string;
	middleName: string;
	lastName: string;
	srcmId: string;
	email: string;
	email2: string;
	mobile: string;
	mobile2: string;
	phone: string;
	phone2: string;
	age: number;
	gender: Gender;
	dateOfBirth: string;
	dateOfJoining: string;
	addressLine1: string;
	addressLine2: string;
	addressLine3: string;
	addressLine4: string;
	cityId: number;
	city: string;
	stateId: number;
	state: string;
	countryId: number;
	country: string;
	postalCode: string;
	centerId: number;
	center: string;
	mySrcmPartnerId: number;
	prefectId: number;
	prefectStatus: string;
	prefectStartDate: string;
	respPrefectId: number;
	respPrefectName: string;
	batch: string;
	zone: string;
	locality: string;
	languages: Array<PreceptorLanguage>;
	preceptorMappedToGroup: boolean;
	preceptorAssignedToMentor: boolean;
	isPrework?: boolean;
	batchId?: boolean;
	region?: string;
	regionId?: string;
	zoneId?: string;
}

export interface PreceptorDashboardCardFilter {
	preceptorMappedToGroup: boolean;
	preceptorAssignedToMentor: boolean;
	preceptorNotMappedToGroup: boolean;
	preceptorNotAssignedToMentor: boolean;
}

export interface PreceptorsFilter {
	srcmId?: string;
	name?: string;
	email?: string;
	mobile?: string;
	countryNames?: Array<string>;
	stateNames?: Array<string>;
	cityNames?: Array<string>;
	// eslint-disable-next-line
	zoneNames?: string | Country;
	centerNames?: Array<string>;
	postalCode?: string;
	zones?: Array<string>;
	batch?: string;
	gender?: Gender;
	prefectStatus?: Array<string>;
	prefectStartDate?: string;
	language?: string;
	respPrefectName?: string;
	showNewPreceptorsForGrouping?: boolean;
	showMentors?: boolean;
	showAllPreceptors?: boolean;
	showPreceptorDashboardCardDetail?: boolean;
	countryIds?: Array<number>;
	stateIds?: Array<number>;
	cityIds?: Array<number>;
	centerIds?: Array<number>;
	sortingRequests?: Array<NPHHSortingRequest>;
	dashboardCardFilterEnabled?: boolean;
	preceptorDashboardCardFilter?: PreceptorDashboardCardFilter;
	excludeIds?: Array<number>;
	regionIds?: Array<number>;
	preceptorBatchId?: number;
	batchId?: number;
	isPrework?: boolean;
}

export interface GetPreceptorsRequest {
	functionarySrcmId: string;
	pageIndex?: number;
	pageSize?: number;
	preceptorsFilter: PreceptorsFilter;
	selectedRoleCode: UserRole;
}

export interface GetPreceptorsResponse {
	preceptors: Array<Preceptor>;
	pagination: Pagination;
}

export interface PreceptorGroup {
	id: number;
	groupNumber: string;
	groupName: string;
	noOfMembers: number;
	mentor: Preceptor;
	functionarySrcmId?: string;
	preceptorGroupMembers: Array<Preceptor>;
}

export interface PreceptorGroupRequest {
	functionarySrcmId: string;
	id: number;
}

export interface CreatePreceptorGroupRequest {
	functionarySrcmId: string;
	preceptorIds: Array<number>;
	preceptorBatchId?: number;
}

export interface CreatePreceptorGroupResponse {
	preceptorGroup: PreceptorGroup;
}

export interface UpdatePreceptorGroupRequest
	extends CreatePreceptorGroupRequest {
	preceptorGroupId: number;
}

export interface UpdatePreceptorGroupResponse {
	preceptorGroup: PreceptorGroup;
}

export interface PreceptorsGroupFilter {
	groupNumber?: string;
	noOfMembers?: number;
	mentorId?: number;
	sortingRequests?: Array<NPHHSortingRequest>;
	preceptorBatchId?: number;
	preceptorSrcmId?: string;
	preceptorName?: string;
	mentorSrcmId?: string;
	mentorName?: string;
}

export interface GetPreceptorGroupRequest {
	groupId: number;
	functionarySrcmId?: string;
}

export interface GetPreceptorsGroupRequest {
	functionarySrcmId: string;
	pageIndex: number;
	pageSize: number;
	preceptorsGroupFilter: PreceptorsGroupFilter;
	selectedRoleCode: UserRole;
}

export interface DeletePreceptorsGroupRequest {
	functionarySrcmId: string;
	preceptorGroupId?: Array<number>;
	preceptorBatchId: number;
}

export interface GetPreceptorsGroupResponse {
	preceptorGroups: Array<PreceptorGroup>;
	pagination: Pagination;
}

export interface AssignMentorRequest {
	functionarySrcmId: string;
	preceptorGroupId: number;
	mentorId: number;
}

export interface GetPreceptorDashboardDetailsRequest {
	functionarySrcmId: string;
}

export interface GetPreceptorDashboardDetailsResponse {
	totalPreceptors: number;
	totalPreceptorsNotMappedWithGroup: number;
	totalPreceptorsMappedWithGroup: number;
	totalPreceptorsNotAssignedWithMentor: number;
	totalPreceptorsAssignedWithMentor: number;
}

export interface PreceptorDashboardCardDetail {
	totalPreceptors: number;
	totalPreceptorsNotMappedWithGroup: number;
	totalPreceptorsMappedWithGroup: number;
	totalPreceptorsNotAssignedWithMentor: number;
	totalPreceptorsAssignedWithMentor: number;
}

export interface Region {
	regionId: number;
	regionName: string;
}

export interface GetRegionsResponse {
	regions: Array<Region>;
	pagination: Pagination;
}

export interface RegionWiseGraphicalView {
	regionId: number;
	region: string;
	preceptorMappedWithGroupCount: number;
	preceptorNotMappedWithGroupCount: number;
	preceptorAssignedWithMentorCount: number;
	preceptorNotAssignedWithMentorCount: number;
}

export interface ZoneWiseGraphicalView {
	zoneId: number;
	zone: string;
	preceptorMappedWithGroupCount: number;
	preceptorNotMappedWithGroupCount: number;
	preceptorAssignedWithMentorCount: number;
	preceptorNotAssignedWithMentorCount: number;
}

export interface GraphicalViewResponse {
	preceptorDashboardCardDetail: PreceptorDashboardCardDetail;
	regionWiseGraphicalViews?: Array<RegionWiseGraphicalView>;
	zoneWiseGraphicalViews?: Array<ZoneWiseGraphicalView>;
}

export interface PreceptorBatchFilter {
	preceptorBatchId?: number;
	batchName?: string;
	batchCode?: string;
	batchDescription?: string;
	startDate?: string;
	endDate?: string;
	statusArray?: Array<Status>;
}

export interface GetPreceptorBatchesRequest {
	functionarySrcmId: string;
	pageIndex: number;
	pageSize: number;
	preceptorBatchFilter: PreceptorBatchFilter;
}

export interface PreceptorBatch {
	id: number;
	key?: number;
	batchName: string;
	batchCode: string;
	description: string;
	startDate: string;
	endDate: string;
	status: Status;
	arePreceptorsLoadedFromMySRCM: boolean;
	totalNoOfPreceptorsToBeLoaded: number;
	totalNoOfPreceptorsLoaded: number;
}

export interface GetPreceptorBatchesResponse {
	preceptorBatches: Array<PreceptorBatch>;
	pagination: Pagination;
}

export interface PreceptorBatchForCreation {
	id?: number;
	batchName: string;
	description: string;
	startDate: string;
	endDate: string;
	status?: Status;
	arePreceptorsLoadedFromMySRCM?: boolean;
	totalNoOfPreceptorsToBeLoaded?: number;
	totalNoOfPreceptorsLoaded?: number;
}

export interface CreatePreceptorBatchRequest {
	functionarySrcmId: string;
	preceptorBatch: PreceptorBatchForCreation;
}

export interface LoadPreceptorsFromMySRCMRequest {
	adminSrcmId: string;
	page?: number;
	pageSize?: number;
	preceptorBatchId?: number;
	selectedRoleCode: UserRole;
}

export interface GetZonesRequest {
	functionarySrcmId: string;
	selectedRoleCode: UserRole;
	preceptorBatchId?: number;
	regionIds?: Array<number>;
	regionId?: number;
	batchId?: number;
	pageIndex?: number;
	pageSize?: number;
}

export interface GetPreceptorByPartnerIdRequest {
	firebaseId: string;
	id: number;
}

export interface CandidateScreeningProgressSummaryFilter {
	regionId?: number;
	zoneNames?: Array<string>;
	gender?: Gender;
	applicantSrcmId?: string;
	searchByApplicantName?: string;
	searchByProposerSrcmId?: string;
	searchByProposerName?: string;
}

export interface CandidateScreeningProgressSummaryRequest {
	functionarySrcmId: string;
	batchId: number;
	filter: CandidateScreeningProgressSummaryFilter;
	selectedRoleCode: UserRole;
}

export interface CandidateScreeningProgressSummaryResponse {
	functionarySrcmId: string;
	batchId: number;
	overallCandidateApplication: number;
	proposerYetToBeAssigned: number;
	waitingForProposer: number;
	proposerProvidedRecommendation: number;
	waitingForApprover: number;
	approvedCandidates: number;
	notGoingForwardCandidates: number;
	waitingForCareTeam: number;
}

export interface CandidateListData {
	id: string;
	count: number;
	description: string;
	imageSrc: string;
}

export interface FunctionariesBulkUploadRequest {
	functionarySrcmId: string;
	functionaryType: string;
}

export interface DownloadCSVReportResponse {
	csvReportSignedUrl: string;
	errorMessage: string;
}

export interface ImportSaveFunctionaryCSVReportRequest {
	functionarySrcmId: string;
	fileNameInCloudStorage: string;
	batchId: number;
	functionaryType: string;
}
export interface ApplicationTableDataType {
	key?: string;
	fullName: string;
	idCard: string;
	gender?: string;
	status?: ReactElement;
	center?: string | undefined;
	zone: string;
	proposerName?: string | undefined;
	proposerSRCMID?: string;
	application?: ReactElement;
	preceptor?: ReactElement;
	candidate?: ReactElement;
	family?: ReactElement;
	otherReference?: ReactElement;
	originalApplication: ApplicationSummary;
	preceptorRecommendation?: ReactElement;
	proposerRecommendation?: ReactElement;
	ZCRecommendation?: ReactElement;
	careTeamRecommendation?: ReactElement;
	applicationStatus?: string;
	preworkStatus?: string;
	originalCandidatePreworkStatus?: PreworkCompletionStatus;
	originalMentorPreworkStatus?: PreworkCompletionStatus;
	disableViewAction?: boolean;
	preworkMentorNextAttemptDate?: string;
	preworkMentorName?: string;
	preworkMentorSrcmId?: string;
	approverRecommendationStatus?: string;
	originalApproverRecommendationStatus?: PreworkReviewAction;
	approverComments?: string;
	forwardedToCareTeamBy?: string;
}

export interface ListAvailableFunctionariesToApplicationRequest {
	applicationId: number;
	pageIndex: number;
	pageSize: number;
	filter: ApplicationsFilter;
}

export interface ReAssignFunctionaryToSelectedApplicationRequest {
	applicationId: number;
	functionaryId: number;
	selectedRoleCode: UserRole;
}

export interface LocationState {
	from?: string;
}

export interface PublishBatchRequest {
	functionarySrcmId: string;
	id: number;
	selectedRoleCode: UserRole;
}

export interface GetRegionZoneCenterRequest {
	applicationId: number;
	functionarySrcmId: string;
	batchId: number;
	centerIdToBeUpdated: number;
	centerNameToBeUpdated: string;
	zoneIdToBeUpdated: number;
	zoneNameToBeUpdated: string;
	regionIdToBeUpdated: number;
	regionNameToBeUpdated: string;
}

export interface RegionZoneCenterResponse {
	centerChanged: boolean;
	proposerReassigned: boolean;
}

export interface RegionZoneCenterParams {
	applicationId: number;
	centerIdToBeUpdated: number;
	centerNameToBeUpdated: string;
	zoneIdToBeUpdated: number;
	zoneNameToBeUpdated: string;
	regionIdToBeUpdated: number;
	regionNameToBeUpdated: string;
}

export interface RegionFilterRequest {
	functionarySrcmId: string;
	searchKey?: string;
	pageIndex?: number;
	pageSize?: number;
	includeOnlyRegionsOfApprover?: boolean;
	displayAllRegions?: boolean;
	isPaginationRequired?: boolean;
}

export interface RegionsPage {
	totalNoOfPages: number;
	regions: Array<{ regionId: string; regionName: string }>;
}

export interface ProposerLoadSummary {
	regionId?: number;
	region?: string;
	totalCandidatesCount: number;
	maleCandidatesCount?: number;
	femaleCandidatesCount?: number;
	totalProposersCount: number;
	maleProposersCount?: number;
	femaleProposersCount?: number;
}

export interface ZonesProposerLoadSummary {
	zoneId?: number;
	zoneName?: string;
	totalCandidates: number;
	maleCandidates?: number;
	femaleCandidates?: number;
	totalProposers: number;
	maleProposers?: number;
	femaleProposers?: number;
	maleRatio?: string;
	femaleRatio?: string;
}

export interface GetProposerLoadSummaryRequest {
	functionarySrcmId: string;
	batchId: number;
	pageIndex: number;
	pageSize: number;
}

export interface GetProposerLoadSummaryResponse {
	proposerLoadSummary?: Array<ProposerLoadSummary>;
	pagination: Pagination;
}

export interface GetZonesProposerLoadSummaryRequest {
	batchId: number;
	regionId: number;
	functionarySrcmId: string;
	pageIndex: number;
	pageSize: number;
}

export interface GetZonesProposerLoadSummaryResponse {
	zonesProposerLoadSummary?: Array<ZonesProposerLoadSummary>;
	pagination: Pagination;
}

export interface CandidateLoadDetails {
	applicationId?: number;
	candidateCenterId?: number;
	candidateZoneId?: number;
	candidateName: string;
	candidateSrcmId: string;
	candidateZoneName: string;
	candidateRegionId?: number;
	candidateRegionName?: string;
	candidateGender: Gender;
	proposerName: string;
	proposerLoad: number;
	proposerGender: Gender;
	proposerSrcmId: string;
}

export interface GetCandidatesLoadSummaryRequest {
	zoneId?: number;
	applicationsFilter: ApplicationsFilter;
	batchId: number;
	functionarySrcmId: string;
	pageIndex: number;
	pageSize: number;
}

export interface GetCandidatesLoadSummaryResponse {
	candidateLoadDetail?: Array<CandidateLoadDetails>;
	zoneName: string;
	pagination: Pagination;
}

export interface ProposerLoadDetail {
	proposerId: number;
	proposerName?: string;
	proposerSrcmId?: string;
	proposerGender?: Gender;
	proposerLoad?: number;
}

export interface ProposerFilter {
	proposerName?: string;
	proposerSrcmId?: string;
}
export interface GetProposersLoadRequest {
	zone?: string;
	batchId: number;
	proposerFilter?: ProposerFilter;
	pageIndex: number;
	pageSize: number;
	applicationId: number;
}

export interface GetProposersLoadResponse {
	proposerLoadDetail?: Array<ProposerLoadDetail>;
	pagination: Pagination;
}
export interface BulkAssignFunctionaryToApplicationForBatchRequest {
	batchId: number;
	regionIds: Array<number>;
	zoneIds: Array<number>;
}

export interface GetEligibleCandidatesForNextBatchRequest {
	functionarySrcmId: string;
	batchId: number;
	pageIndex: number;
	pageSize: number;
	applicationsFilter: ApplicationsFilter;
	selectedRoleCode: UserRole;
}

export interface GetEligibleCandidatesForNextBatchResponse {
	applicationSummary: Array<ApplicationSummary>;
	pagination: Pagination;
}

export interface GetSendEmailToEligibleBatchRequest {
	functionarySrcmId: string;
	eligibleBatchId: number;
	requestedApplicationIds: Array<number>;
	selectedRoleCode: UserRole;
}
